import {isPossiblePhoneNumber} from "libphonenumber-js";
import {FocusEvent, KeyboardEvent, MouseEvent, useState} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {ParsedCountry} from "react-international-phone";

import {Button} from "@/components/button/Button.tsx";
import {PhoneInput} from "@/components/phone/PhoneInput.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

import styles from "@/components/form/fields/PhoneField.module.scss";
import "react-international-phone/style.css";

export const PhoneListInput = ({name, ...inputProps}: FieldProps) => {
  const {
    getValues,
    control,
    clearErrors,
    setError,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  const {prepend} = useFieldArray({control, name});
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneValueFormatted, setPhoneValueFormatted] = useState("");
  const hasValue = phoneValueFormatted.split(" ")[1]?.length > 0;

  const addToList = (
    event: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (!phoneValueFormatted.trim() || !hasValue) {
      return;
    }
    if (!isPossiblePhoneNumber(phoneValue)) {
      setError(name, {message: "Please enter a valid phone number."});
      return;
    }
    if (!getValues(name).includes(phoneValue)) {
      prepend(phoneValue);
    }
    setPhoneValue("");
    // PhoneInput does not report the reset through onChange, so the formatted copy that
    // backs hasValue has to be cleared here or the emptied input still looks filled in.
    setPhoneValueFormatted("");
  };

  return (
    <div className="d-flex gap-2">
      <PhoneInput
        name={name}
        inputProps={{
          ...inputProps,
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              addToList(event);
            }
          },
          onBlur: addToList,
        }}
        inputClassName={styles.phoneInput}
        value={phoneValue}
        onChange={(phone: string, meta: {country: ParsedCountry; inputValue: string}) => {
          setPhoneValue(phone);
          setPhoneValueFormatted(`+${meta.country.dialCode} ${meta.inputValue}`);
          clearErrors(name);
        }}
      />
      <Button onClick={addToList} disabled={!hasValue || !!fieldError}>
        Add
      </Button>
    </div>
  );
};
